import { useState, useEffect, useLayoutEffect } from "react";
import { Box, Container, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";

import { useNav } from "../../../context/NavProvider";
import { useArrangement } from "../../../context/ArrangementProvider";
import { useLiveLoans } from "../../../context/LiveLoansProvider";

import CurrentRepaymentDate from "./CurrentRepaymentDate";
import CalculateNewRepaymentDate from "./CalculateNewRepaymentDate";
import CalculationResult from "./CalculationResult";
import ChangePaymentDateConfirmation from "./ChangePaymentDateConfirmation";


/**
 * Live loans change payment date page component
 * @returns
 */
function ChangePaymentDatePage() {

  const { setShowFooter } = useNav();
  setShowFooter(false);

  const {
    useGetArrangement,
    useGetArrangementHistory,
    loadPlanData,
    planLoaded,
    lastSavedPlan
  } = useArrangement();

  const { arrangement, isArrangementLoading } = useGetArrangement();
  const { arrangementHistory, isArrangementHistoryLoading } = useGetArrangementHistory();

  const {
    useGetLiveAccount,
    useGetPaymentDateChanges,
    allowPaymentDateChanges
  } = useLiveLoans();

  const { liveAccount, isLiveAccountLoading } = useGetLiveAccount();
  const { paymentDateChanges, isPaymentDateChangesLoading } = useGetPaymentDateChanges();

  const navigate = useNavigate();

  const [ proposedDate, setProposedDate ] = useState(dayjs());
  const [ calculationResponse, setCalculationResponse ] = useState(null);
  const [ paymentDateChangeSaved, setPaymentDateChangeSaved ] = useState(false);

  useLayoutEffect(() => {
    // Redirect to home and do not allow plan changes in payment window
    if ((lastSavedPlan && lastSavedPlan?.inPaymentWindow === true) || 
      (paymentDateChanges && !allowPaymentDateChanges(paymentDateChanges))) {
      navigate("/");
    };
  }, [lastSavedPlan, paymentDateChanges]);

  useEffect(() => {
    // Redirect to home and do not allow plan changes in payment window
    if ((lastSavedPlan && lastSavedPlan?.inPaymentWindow === true) || 
      (paymentDateChanges && !allowPaymentDateChanges(paymentDateChanges))) {
      navigate("/");
    };
  }, [lastSavedPlan, paymentDateChanges]);

  useEffect(() => {
    if (
      !isArrangementLoading &&
      !isArrangementHistoryLoading &&
      !planLoaded
    ) {
      loadPlanData(arrangement, arrangementHistory);
    }
  }, [
    planLoaded,
    isArrangementLoading,
    isArrangementHistoryLoading,
    loadPlanData,
    arrangement,
    arrangementHistory
  ]);

  if (
    isLiveAccountLoading ||
    isPaymentDateChangesLoading ||
    isArrangementLoading ||
    isArrangementHistoryLoading
  ) {
    return <LinearProgress />;
  }

  return (
    liveAccount &&
    lastSavedPlan &&
    lastSavedPlan?.inPaymentWindow === false &&
    planLoaded && (
    <>
      <Container sx={{ mt: 4, mb: 7, pb: "350px" }}>
        <Typography sx={{ fontSize: 20, fontWeight: "bold", mb: 2 }}>
            Repayment Date Change
        </Typography>
        {!paymentDateChangeSaved ? (
          <>
            <Box>
              <CurrentRepaymentDate liveAccount={liveAccount} />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "50%" }, pt: 4 }}>
              <CalculateNewRepaymentDate
                liveAccount={liveAccount}
                proposedDate={proposedDate}
                setProposedDate={setProposedDate}
                setResponse={setCalculationResponse}
              />
            </Box>
            {calculationResponse &&
              <Box sx={{ pt: 5 }}>
                <CalculationResult
                  liveAccount={liveAccount}
                  proposedDate={proposedDate}
                  response={calculationResponse}
                  setPaymentDateChangeSaved={setPaymentDateChangeSaved}
                />
              </Box>
            }
          </>
        ) : (
          <Box>
            <ChangePaymentDateConfirmation
              liveAccount={liveAccount}
              response={calculationResponse}
            />
          </Box>
        )}
      </Container>
      {setShowFooter(true)}
    </>
    )
  );
}

export default ChangePaymentDatePage;
